/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { RENEWAL, RENEWAL_PAYMENT, RENEWAL_PREVIOUS_PAYMENT } from 'constants/routeConstants';
import useCosmosTrackEvent from 'hooks/useCosmosTrackEvent';
import Button from 'shared/components/Button/Button';
import useReferrerParam from 'hooks/useReferrerParam';
import calculateBasketPricing from 'views/MyRac/ExpiryRenewal/utils/pricingUtils';

import { Store } from 'types/store';
import Radio from './common-components/Radio/Radio';

import MastercardLogo from './assets/svg/mastercard.svg';
import VisaLogo from './assets/svg/visa.svg';
import Stepper from './components/organisms/Stepper';
import Banner from './components/molecules/Banner';
import PaymentHeader from './components/molecules/PaymentHeader';

import './expiryRenewalGlobals.scss';

const PaymentOptions = () => {
  const { pageModel, addOns, amendments } = useSelector((state: Store) => state.expiryRenewal);

  const navigate = useNavigate();

  const isAppView = useReferrerParam();

  const { previousCardDetails } = pageModel || {};
  const selectedAddOns = addOns?.selected ?? [];
  // Narrow on productCode too - "amendments" also carries ExtraPerson adds.
  const vehiclesAdded = (amendments ?? []).filter((a) => a.amendmentType === 'Add' && a.productCode === 'ExtraVehicle').length;
  const peopleAdded = (amendments ?? []).filter((a) => a.amendmentType === 'Add' && a.productCode === 'ExtraPerson').length;

  // Previous card is reached only for non-monthly-flex payments. Reuse the shared
  // basket pricing util so add-ons (scaled by the added vehicle/person count) and the
  // added vehicle and person amendments are all included in the total.
  const { totalPrice, addOnTotal, wasTotal } = calculateBasketPricing(selectedAddOns, false, pageModel, vehiclesAdded, peopleAdded);
  const annualTotal = totalPrice + addOnTotal;
  const annualWas = wasTotal;

  const RADIOS = {
    Prev: 'usePreviousCard',
    New: 'addNewCard',
  } as const;

  const [selected, setSelected] = useState<string>(RADIOS.Prev);

  const { cosmosTrackEvent } = useCosmosTrackEvent('ExpiryRenewals');

  const handleChange = (radio: string) => {
    setSelected(radio);
  };

  const chooseOption = async () => {
    if (selected === RADIOS.Prev) {
      navigate(RENEWAL_PREVIOUS_PAYMENT, { replace: true });
    } else {
      navigate(RENEWAL_PAYMENT, { replace: true });
    }
  }

  const formatMonthYear = (date: Date) => {
    const month = date.getMonth() + 1; // getMonth() is 0-based
    const year = date.getFullYear();
    return `${month}/${year}`;
  };

  useEffect(() => {
    cosmosTrackEvent('renewal_journey_payment_options');
  }, []);

  useEffect(() => {
    if (!previousCardDetails) {
      navigate(RENEWAL, { replace: true });
    }
  }, [previousCardDetails, navigate]);

  return (
    <>
      {pageModel?.discountModel?.isDiscountedPrice && (
        <Banner />
      )}
      <Stepper step={4} />
      <div className={clsx('fs-6 mb-5 text-secondary', isAppView ? 'm-3 p-3 bg-white border rounded' : 'p-4')}>
        {pageModel && (
          <PaymentHeader
            smallHeader={pageModel?.isMonthlyLongerTermPolicy ? 'Pay monthly' : 'Pay annually'}
            mainHeader="One-time card payment"
            description={pageModel?.isMonthlyLongerTermPolicy && 'We can’t always split the cost of your cover into equal monthly payments.'}
            price={annualTotal}
            priceWas={annualWas}
          />
        )}
        <button type="button" className="d-block w-100 bg-body border rounded p-3 mb-4" onClick={() => handleChange(RADIOS.Prev)}>
          <div className="d-flex align-items-center fw-bold fs-6">
            <Radio
              label="Use previous payment method"
              onChange={() => {}}
              checked={selected === RADIOS.Prev}
              key={RADIOS.Prev}
            />
          </div>
          {previousCardDetails && (
          <div className="d-flex align-items-center mx-4 px-3 mt-1 fw-bold fs-6">
            <img src={previousCardDetails.cardType === 'Visa' ? VisaLogo : MastercardLogo} alt={previousCardDetails.cardType} />
            <div className="mx-3 text-main">
              {previousCardDetails.cardType} {previousCardDetails.maskedPanLastDigits.replaceAll('*', '•')}
              <span className="d-block text-secondary lh-1 fw-light">{formatMonthYear(new Date(previousCardDetails.expiryDate))}</span>
            </div>
          </div>
              )}
        </button>
        <button type="button" className="d-block w-100 bg-body border rounded p-3 mb-4" onClick={() => handleChange(RADIOS.New)}>
          <div className="fw-bold fs-6">
            <Radio
              label="Add a new card"
              onChange={() => {}}
              checked={selected === RADIOS.New}
              key={RADIOS.New}
            />
          </div>
        </button>
        <Button
          onClickHandler={chooseOption}
          buttonText="Continue"
          large
        />
      </div>
    </>
  );
};

export default PaymentOptions;
